import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { AlertOctagon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * InjuryEventLogger
 * ─────────────────
 * Coach-facing modal + timeline for logging labeled injury events.
 * Every event becomes training data for the future risk_v2 ML model.
 *
 * Renders:
 *  - "Log injury event" button that opens a 7-field form
 *  - Chronological list of past events with delete button (coach only)
 */
const BODY_REGIONS = [
  { v: "shoulder", label: "Shoulder" },
  { v: "elbow", label: "Elbow" },
  { v: "forearm", label: "Forearm / wrist" },
  { v: "wrist_hand", label: "Wrist / hand" },
  { v: "upper_back", label: "Upper back / T-spine" },
  { v: "lower_back", label: "Lower back / L-spine" },
  { v: "hip", label: "Hip / groin" },
  { v: "knee", label: "Knee" },
  { v: "ankle_foot", label: "Ankle / foot" },
  { v: "other", label: "Other" },
];
const SIDES = [
  { v: "dominant", label: "Dominant" },
  { v: "non_dominant", label: "Non-dominant" },
  { v: "bilateral", label: "Bilateral" },
  { v: "n_a", label: "N/A" },
];
const SEVERITIES = [
  { v: "mild", label: "Mild · no missed days" },
  { v: "moderate", label: "Moderate · <14 days out" },
  { v: "severe", label: "Severe · >14 days out" },
  { v: "surgical", label: "Surgical" },
];
const MECHANISMS = [
  { v: "acute_throwing", label: "Acute · during throwing" },
  { v: "gradual_overuse", label: "Gradual · overuse" },
  { v: "non_throwing_athletic", label: "Non-throwing athletic" },
  { v: "non_athletic", label: "Non-athletic (fell, etc.)" },
  { v: "unknown", label: "Unknown" },
];

const initialForm = () => ({
  body_region: "shoulder",
  side: "dominant",
  severity: "mild",
  mechanism: "gradual_overuse",
  onset_date: new Date().toISOString().slice(0, 10),
  diagnosis: "",
  days_missed: "",
  notes: "",
});

export default function InjuryEventLogger({ playerUserId, isCoach = true }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!playerUserId) return;
    try {
      const { data } = await api.get(`/injury-risk/${playerUserId}/events`);
      setEvents(data?.events || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerUserId]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.days_missed = payload.days_missed === "" ? null : parseInt(payload.days_missed, 10);
      payload.diagnosis = payload.diagnosis || null;
      payload.notes = payload.notes || null;
      await api.post(`/injury-risk/${playerUserId}/events`, payload);
      toast.success("Injury event logged — added to training dataset");
      setForm(initialForm());
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (eventId) => {
    if (!window.confirm("Delete this event? It will be removed from the training set.")) return;
    try {
      await api.delete(`/injury-risk/${playerUserId}/events/${eventId}`);
      toast.success("Event removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section className="rounded-xl ink-card p-5" data-testid="injury-event-logger">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <AlertOctagon className="h-3.5 w-3.5 text-red-400" /> Injury history
          </div>
          <div className="text-xs text-white/40 mt-1">
            {events.length} event(s) · feeds risk_v2 training dataset
          </div>
        </div>
        {isCoach && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="log-injury-event-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Log injury event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">Log an injury event</DialogTitle>
                <DialogDescription className="text-white/55 text-sm">
                  Recorded as a label for the ArmIQ predictive model. Coaches, ATs, and player-development can log — every event improves risk_v2.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="injury-event-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/70">Body region</Label>
                    <select value={form.body_region} onChange={upd("body_region")}
                      className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white"
                      data-testid="injury-body-region">
                      {BODY_REGIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/70">Side</Label>
                    <select value={form.side} onChange={upd("side")}
                      className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white"
                      data-testid="injury-side">
                      {SIDES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/70">Severity</Label>
                    <select value={form.severity} onChange={upd("severity")}
                      className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white"
                      data-testid="injury-severity">
                      {SEVERITIES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/70">Mechanism</Label>
                    <select value={form.mechanism} onChange={upd("mechanism")}
                      className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white"
                      data-testid="injury-mechanism">
                      {MECHANISMS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/70">Onset date</Label>
                    <Input type="date" required value={form.onset_date} onChange={upd("onset_date")}
                      className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white"
                      data-testid="injury-onset-date" />
                  </div>
                  <div>
                    <Label className="text-white/70">Days missed</Label>
                    <Input type="number" min="0" max="365" value={form.days_missed} onChange={upd("days_missed")}
                      placeholder="Optional" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white"
                      data-testid="injury-days-missed" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Diagnosis (optional)</Label>
                  <Input value={form.diagnosis} onChange={upd("diagnosis")}
                    placeholder="e.g., UCL sprain grade II"
                    className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white"
                    data-testid="injury-diagnosis" />
                </div>
                <div>
                  <Label className="text-white/70">Notes</Label>
                  <Textarea value={form.notes} onChange={upd("notes")}
                    placeholder="Free-form context — mechanism details, treatment, etc."
                    className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white min-h-[80px]"
                    data-testid="injury-notes" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving}
                    className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}
                    data-testid="injury-event-submit">
                    {saving ? "Saving…" : "Log event"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {events.length === 0 ? (
        <div className="text-sm text-white/40 py-4" data-testid="injury-events-empty">
          No injury history logged. Every event you record helps ArmIQ predict future risk more precisely.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--ink-border)]" data-testid="injury-events-list">
          {events.map((ev) => (
            <li key={ev.event_id} className="py-3 flex items-start justify-between gap-3"
              data-testid={`injury-event-${ev.event_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white font-semibold capitalize">
                  {ev.body_region.replace("_", " ")} · {ev.side.replace("_", " ")}
                  <span className="ml-2 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-bold border"
                    style={{
                      color: ev.severity === "surgical" || ev.severity === "severe" ? "#EF4444"
                        : ev.severity === "moderate" ? "#F59E0B" : "#22D3EE",
                      borderColor: "currentColor",
                    }}>
                    {ev.severity}
                  </span>
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  {ev.onset_date} · {ev.mechanism.replace(/_/g, " ")}
                  {ev.days_missed != null && ` · ${ev.days_missed} days missed`}
                </div>
                {ev.diagnosis && <div className="text-xs text-white/60 mt-1">Dx: {ev.diagnosis}</div>}
                {ev.notes && <div className="text-xs text-white/40 mt-1 italic">{ev.notes}</div>}
              </div>
              {isCoach && (
                <Button variant="ghost" size="icon" onClick={() => remove(ev.event_id)}
                  data-testid={`delete-injury-event-${ev.event_id}`}>
                  <Trash2 className="h-4 w-4 text-white/40 hover:text-red-400" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
